// components/EventRegistrationForm/GroupForm.js
import React, { useRef, useState, useEffect } from 'react';
import { Plus, Trash2, GroupIcon, Smartphone, AtSign, User, Check, Loader2, Key, CheckSquare, Users, Building, MapPin, Hash, Camera, Upload, X, Info } from 'lucide-react';
import Swal from 'sweetalert2';
import { getData } from '../../../../services/apiService';

const GroupForm = ({
    groupMembers,
    render,
    honorificsOptions,
    maxMembers,
    handleGroupMemberChange,
    handleVerifyContact,
    addGroupMember,
    removeGroupMember,
    participantTypes,
    getFieldDisabledStatus,
    verificationInProgress,
    otpSending,
    onFormDataChange,
    eventId,
    handlePhoneBlur,
    isGroupPayment,
    setIsGroupPayment,
    verificationKey,
    setRender,
    countries
}) => {
    const fileInputRefs = useRef({});
    const [isParticipantTypeLocked, setIsParticipantTypeLocked] = useState(false);
    const [localRender, setLocalRender] = useState(false); // ADD LOCAL STATE

    const prevMembersCountRef = useRef(groupMembers.length);
    // Add this near the top of GroupForm component with other useEffects
    useEffect(() => {
        setLocalRender(prev => !prev);
    }, [groupMembers.map(m => `${m.id}-${m.phoneVerified}-${m.verificationStep}`).join(',')]);


    useEffect(() => {
        if (groupMembers.length > 0 && groupMembers[0].participantId) {
            setIsParticipantTypeLocked(true);
        } else {
            setIsParticipantTypeLocked(false);
        }
    }, [groupMembers]);
    useEffect(() => {
        setRender(prev => !prev);
    }, [verificationKey]);

    useEffect(() => {
        const firstMember = groupMembers[0];
        if (firstMember && firstMember.participantId && firstMember.role) {
            let hasUpdates = false;

            groupMembers.forEach((member, index) => {
                if (index > 0) {
                    if (member.participantId !== firstMember.participantId || member.role !== firstMember.role) {
                        handleGroupMemberChange({
                            target: {
                                name: 'role',
                                value: firstMember.role
                            }
                        }, member.id);

                        handleGroupMemberChange({
                            target: {
                                name: 'participantId',
                                value: firstMember.participantId
                            }
                        }, member.id);
                        hasUpdates = true;
                    }
                }
            });

            if (hasUpdates && onFormDataChange) {
                setTimeout(() => {
                    onFormDataChange(prev => ({ ...prev }));
                }, 100);
            }
        }
    }, [groupMembers[0]?.participantId, groupMembers[0]?.role, onFormDataChange, render]);

    // Effect to auto-populate newly added members with better handling
    useEffect(() => {
        const currentCount = groupMembers.length;

        // Check if a new member was added
        if (currentCount > prevMembersCountRef.current) {
            // Get the newly added member (the last one)
            const newMember = groupMembers[currentCount - 1];

            // If the new member has a participantId (auto-filled from first member)
            if (newMember && newMember.participantId) {
                // Small delay to ensure state is updated
                setTimeout(() => {
                    // Fetch amount for the new member
                    const fetchAmountForNewMember = async () => {
                        try {
                            // Set fetching state
                            handleGroupMemberChange({
                                target: {
                                    name: 'fetchingAmount',
                                    value: true
                                }
                            }, newMember.id);

                            const response = await getData(`Event/GetAmount?eventId=${eventId}&participantTypeId=${newMember.participantId}`);

                            if (response && response.isError === false && response.result) {
                                const result = response.result;
                                const totalAmount = parseFloat(result.TotAmt) || 0;

                                // Update amount
                                handleGroupMemberChange({
                                    target: {
                                        name: 'amount',
                                        value: totalAmount
                                    }
                                }, newMember.id);

                                // Update amount details
                                handleGroupMemberChange({
                                    target: {
                                        name: 'amountDetails',
                                        value: {
                                            gstPercentage: parseFloat(result.Gst_Per) || 0,
                                            amountWithoutGst: parseFloat(result.WithOutGstAmt) || 0,
                                            gstAmount: parseFloat(result.Gst_Amt) || 0,
                                            totalAmount: totalAmount
                                        }
                                    }
                                }, newMember.id);

                                // Notify parent to recalculate total
                                if (onFormDataChange) {
                                    onFormDataChange(prev => ({ ...prev }));
                                }
                            }
                        } catch (error) {
                            console.error('Error fetching amount for new member:', error);
                        } finally {
                            // Clear fetching state
                            handleGroupMemberChange({
                                target: {
                                    name: 'fetchingAmount',
                                    value: false
                                }
                            }, newMember.id);
                        }
                    };

                    fetchAmountForNewMember();
                }, 200);
            }
        }

        // Update the ref with current count
        prevMembersCountRef.current = currentCount;
    }, [groupMembers.length, eventId, onFormDataChange]);

    // Ensure all non-first members have participant type from first member on initial load
    useEffect(() => {
        const firstMember = groupMembers[0];
        if (firstMember && firstMember.role && firstMember.participantId) {
            let hasUpdates = false;

            // Check each member and update if needed
            groupMembers.forEach((member, index) => {
                if (index > 0 && (!member.role || member.role === '' || member.role !== firstMember.role)) {
                    handleGroupMemberChange({
                        target: {
                            name: 'role',
                            value: firstMember.role
                        }
                    }, member.id);

                    handleGroupMemberChange({
                        target: {
                            name: 'participantId',
                            value: firstMember.participantId
                        }
                    }, member.id);
                    hasUpdates = true;
                }
            });

            // If updates were made, trigger a form data refresh
            if (hasUpdates && onFormDataChange) {
                setTimeout(() => {
                    onFormDataChange(prev => ({ ...prev }));
                }, 100);
            }
        }
    }, []); // Run once on initial load

    useEffect(() => {
        const fetchAmountForMember = async (member) => {
            // Add validation for eventId
            if (!eventId) {
                console.error('Event ID is undefined in fetchAmountForMember');
                return;
            }

            if (!member.participantId || member.amount || member.fetchingAmount) return;

            // Skip if member is already paid
            if (member.isExistingUser && member.isAlreadyPaid) {
                return;
            }

            // Set fetching state
            handleGroupMemberChange({
                target: {
                    name: 'fetchingAmount',
                    value: true
                }
            }, member.id);

            try {
                const response = await getData(`Event/GetAmount?eventId=${eventId}&participantTypeId=${member.participantId}`);

                if (response && response.isError === false && response.result) {
                    const result = response.result;
                    const totalAmount = parseFloat(result.TotAmt) || 0;

                    // Update amount
                    handleGroupMemberChange({
                        target: {
                            name: 'amount',
                            value: totalAmount
                        }
                    }, member.id);

                    // Update amount details
                    handleGroupMemberChange({
                        target: {
                            name: 'amountDetails',
                            value: {
                                gstPercentage: parseFloat(result.Gst_Per) || 0,
                                amountWithoutGst: parseFloat(result.WithOutGstAmt) || 0,
                                gstAmount: parseFloat(result.Gst_Amt) || 0,
                                totalAmount: totalAmount
                            }
                        }
                    }, member.id);

                    // Notify parent to recalculate total
                    if (onFormDataChange) {
                        onFormDataChange(prev => ({ ...prev }));
                    }
                }
            } catch (error) {
                console.error('Error fetching amount for member:', error);
            } finally {
                // Clear fetching state
                handleGroupMemberChange({
                    target: {
                        name: 'fetchingAmount',
                        value: false
                    }
                }, member.id);
            }
        };

        // Check each member and fetch amount if needed
        groupMembers.forEach(member => {
            if (member.participantId && !member.amount && !member.fetchingAmount) {
                fetchAmountForMember(member);
            }
        });
    }, [groupMembers.map(m => m.participantId).join(','), eventId]);

    // Define gender options
    const genderOptions = [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
        { value: 'O', label: 'Other' },
    ];

    // Helper function to check if member field is filled
    const getMemberFieldStatus = (member, fieldName) => {
        const value = member[fieldName];
        return value && value.toString().trim() !== '';
    };

    // Check if any member has paid status 1
    const hasAnyPaidMember = () => {
        return groupMembers.some(member =>
            member.isExistingUser &&
            member.isAlreadyPaid &&
            member.paidStatus === 1
        );
    };

    // Fetch pincode details for a group member
    const fetchPincodeDetailsForMember = async (pincode, memberId) => {
        if (!pincode || pincode.length !== 6) return;

        try {
            const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
            const data = await response.json();

            if (data && data[0] && data[0].Status === "Success") {
                const postOffice = data[0].PostOffice[0];

                // Update member's state and district
                handleGroupMemberChange({
                    target: {
                        name: 'State',
                        value: postOffice.State
                    }
                }, memberId);

                handleGroupMemberChange({
                    target: {
                        name: 'District',
                        value: postOffice.District
                    }
                }, memberId);

                // Show success message
                setTimeout(() => {
                    Swal.fire({
                        title: 'Location Found!',
                        text: `State: ${postOffice.State}, District: ${postOffice.District}`,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#f8fafc',
                        color: '#1f2937'
                    });
                }, 100);
            } else {
                Swal.fire({
                    title: 'Invalid Pincode',
                    text: 'Could not find location for this pincode. Please enter manually.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    background: '#f8fafc',
                    color: '#1f2937'
                });
            }
        } catch (error) {
            console.error('Error fetching pincode details:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch location details. Please enter manually.',
                icon: 'error',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
        }
    };

    // Handle pincode change for a member
    const handleMemberPincodeChange = async (e, memberId) => {
        const pincode = e.target.value;

        // Update the pincode
        handleGroupMemberChange(e, memberId);

        // Fetch pincode details after a delay
        if (pincode.length === 6) {
            setTimeout(() => {
                fetchPincodeDetailsForMember(pincode, memberId);
            }, 1000);
        }
    };

    // Handle member file select
    const handleMemberFileSelect = (e, memberId) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate file size (max 512KB)
        if (file.size > 512 * 1024) {
            Swal.fire({
                title: 'File too large',
                text: 'Please upload an image less than 512 KB',
                icon: 'error',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            // Reset the file input
            if (fileInputRefs.current[memberId]) {
                fileInputRefs.current[memberId].value = '';
            }
            return;
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            Swal.fire({
                title: 'Invalid file type',
                text: 'Please upload JPG, PNG, or WEBP image',
                icon: 'error',
                confirmButtonText: 'OK',
                background: '#f8fafc',
                color: '#1f2937'
            });
            // Reset the file input
            if (fileInputRefs.current[memberId]) {
                fileInputRefs.current[memberId].value = '';
            }
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            // Update member with both file and preview
            handleGroupMemberChange({
                target: {
                    name: 'photoFile',
                    value: file
                }
            }, memberId);

            // Also update preview separately
            handleGroupMemberChange({
                target: {
                    name: 'photoPreview',
                    value: reader.result
                }
            }, memberId);
        };
        reader.readAsDataURL(file);
    };

    // Check if a member has already paid
    const isMemberPaid = (member) => {
        return member.isExistingUser &&
            member.isAlreadyPaid &&
            member.paidStatus === 1 &&
            member.participantId &&
            member.paidParticipantTypeId &&
            parseInt(member.participantId) === parseInt(member.paidParticipantTypeId);
    };

    // Check if a specific participant type is disabled for a member
    const isParticipantTypeDisabled = (participantId, member, index) => {
        // For non-first members, always disable if participant type is locked
        if (index > 0 && isParticipantTypeLocked) {
            return true;
        }

        // Only check paid status for existing users
        if (member.isExistingUser && member.isAlreadyPaid && member.paidStatus === 1) {
            const participantIdNum = parseInt(participantId);
            const paidParticipantTypeId = parseInt(member.paidParticipantTypeId);
            return participantIdNum === paidParticipantTypeId;
        }

        return false;
    };

    // Get the display text for participant type option
    const getParticipantOptionDisplay = (participant, member, index) => {
        const isPaid = member.isExistingUser &&
            member.isAlreadyPaid &&
            member.paidStatus === 1 &&
            parseInt(member.paidParticipantTypeId) === participant.Participant_ID;

        // Only show "(Already Paid)" for members who actually have paid status
        if (isPaid) {
            return `${participant.Participant_Name} (Already Paid)`;
        }

        return participant.Participant_Name;
    };
    const groupMembersRef = useRef(groupMembers);

    useEffect(() => {
        groupMembersRef.current = groupMembers;
    }, [groupMembers]);
    useEffect(() => {
        // Force re-render when verificationKey changes (for non-Indian number verification)
        if (setRender) {
            setRender(prev => !prev);
        }
    }, [verificationKey, setRender]);
    const handleAddMember = () => {
        const currentMembers = groupMembersRef.current;
        const firstMember = currentMembers[0];

        // Check if we've reached maximum members
        if (currentMembers.length >= maxMembers) {
            Swal.fire({
                title: 'Maximum Members Reached',
                text: `You can only add up to ${maxMembers} members in a group.`,
                icon: 'warning',
                confirmButtonText: 'OK',
                timer: 2000
            });
            return;
        }

        // Check if lead member has completed participant type selection
        if (!firstMember?.participantId || !firstMember?.role) {
            Swal.fire({
                title: 'Complete Lead Member First',
                html: `
                    <div class="text-left">
                        <p class="mb-2">Please complete the lead member's information before adding more members:</p>
                        <p class="mt-3 text-sm text-amber-600">The lead member must be fully registered first.</p>
                    </div>
                `,
                icon: 'info',
                confirmButtonText: 'Got it',
                background: '#f8fafc',
                color: '#1f2937',
                confirmButtonColor: '#8b5cf6'
            });
            return;
        }

        // Check if lead member has completed all required fields (optional but recommended)
        const isLeadComplete = firstMember.phoneVerified &&
            firstMember.emailVerified &&
            firstMember.fullName &&
            firstMember.Ins_Name &&
            firstMember.gender &&
            firstMember.photoPreview;

        if (!isLeadComplete) {
            Swal.fire({
                title: 'Complete Lead Member First',
                html: `
                    <div class="text-left">
                        <p class="mb-2 font-semibold">Please complete the lead member's registration first:</p>
                        <div class="bg-gray-50 p-3 rounded-lg mb-2">
                            ${!firstMember.phoneVerified ? '❌ Verify phone number<br>' : '✅ Phone verified<br>'}
                            ${!firstMember.emailVerified ? '❌ Verify email address<br>' : '✅ Email verified<br>'}
                            ${!firstMember.fullName ? '❌ Enter full name<br>' : '✅ Full name added<br>'}
                            ${!firstMember.gender ? '❌ Select gender<br>' : '✅ Gender selected<br>'}
                            ${!firstMember.Ins_Name ? '❌ Enter institution name<br>' : '✅ Institution added<br>'}
                            ${!firstMember.photoPreview ? '❌ Upload profile photo' : '✅ Photo uploaded'}
                        </div>
                        <p class="text-sm text-blue-600">Complete all required fields for the lead member before adding team members.</p>
                    </div>
                `,
                icon: 'info',
                confirmButtonText: 'Complete Lead Member',
                background: '#f8fafc',
                color: '#1f2937',
                confirmButtonColor: '#8b5cf6'
            });
            return;
        }

        // All good - add the member
        addGroupMember();

        // Show success message
        Swal.fire({
            title: 'Member Added!',
            text: `You can now add details for member ${currentMembers.length + 1}`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
            background: '#f8fafc',
            color: '#1f2937'
        });
    };


    // Dedicated useEffect for auto-populating new members
    useEffect(() => {
        const members = groupMembersRef.current;

        // Find any member that has no participantId but isn't the first member
        const unpopulatedMember = members.find((member, index) =>
            index > 0 && (!member.participantId || member.participantId === '')
        );

        if (unpopulatedMember && members[0]?.participantId) {
            const firstMember = members[0];
            const memberId = unpopulatedMember.id;

            // Populate the member
            handleGroupMemberChange({
                target: { name: 'role', value: firstMember.role }
            }, memberId);

            handleGroupMemberChange({
                target: { name: 'participantId', value: firstMember.participantId }
            }, memberId);

            // Fetch amount
            const fetchAmount = async () => {
                if (!eventId || !firstMember.participantId) return;

                try {
                    const response = await getData(`Event/GetAmount?eventId=${eventId}&participantTypeId=${firstMember.participantId}`);
                    if (response && !response.isError && response.result) {
                        handleGroupMemberChange({
                            target: { name: 'amount', value: parseFloat(response.result.TotAmt) || 0 }
                        }, memberId);
                    }
                } catch (error) {
                    console.error('Error fetching amount:', error);
                }
            };

            fetchAmount();
        }
    }, [groupMembers.length]); // Only runs when length changes
    // Get the participant type name for display - FIXED to handle undefined/empty values
    const getParticipantTypeName = (member) => {
        if (!member.role || member.role === '') {
            // If no participant type is set but first member has one, try to get it
            if (groupMembers.length > 0 && groupMembers[0].role) {
                return groupMembers[0].role;
            }
            return 'Not selected';
        }
        return member.role;
    };

    // Function to check if a member is complete (all required fields filled)
    const isMemberComplete = (member) => {
        // Check if member is paid - if paid, consider complete
        if (isMemberPaid(member)) {
            return true;
        }

        // Check verification status
        if (!member.phoneVerified || !member.emailVerified) {
            return false;
        }

        // Check required fields
        const requiredFields = [
            'Prefix', 'fullName', 'gender', 'Ins_Name',
            'Ins_Address', 'PinCode', 'State', 'District'
        ];

        const allFieldsFilled = requiredFields.every(field =>
            member[field] && member[field].toString().trim() !== ''
        );

        // Check if photo is uploaded
        const hasPhoto = member.photoPreview || member.photoFile;

        // Check if participant type is selected (for first member)
        const hasParticipantType = member.role && member.role !== '';

        return allFieldsFilled && hasPhoto && hasParticipantType;
    };

    // Calculate completion percentage for a member
    const getMemberCompletionPercentage = (member) => {
        if (isMemberPaid(member)) return 100;

        const requiredFields = [
            'phoneVerified', 'emailVerified', 'Prefix', 'fullName',
            'gender', 'Ins_Name', 'Ins_Address', 'PinCode',
            'State', 'District', 'photoPreview', 'role'
        ];

        const completedFields = requiredFields.filter(field => {
            if (field === 'phoneVerified' || field === 'emailVerified') {
                return member[field] === true;
            }
            if (field === 'photoPreview') {
                return !!member[field];
            }
            return member[field] && member[field].toString().trim() !== '';
        });

        return Math.round((completedFields.length / requiredFields.length) * 100);
    };

    // Handle group payment toggle - FIXED: Added proper event handling
    const handleGroupPaymentToggle = (e) => {
        e.preventDefault(); // Prevent any form submission
        e.stopPropagation(); // Stop event bubbling
        setIsGroupPayment(!isGroupPayment);
    };

    return (
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-3 sm:p-4 border border-purple-200">
            {/* Group Payment Toggle Switch - FIXED: Added proper onClick handler */}
            <div className="mb-6 p-4 bg-white rounded-lg border border-purple-200 shadow-sm">
                <div className="sm:flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm">

                    <label className="text-sm font-medium text-gray-700 px-3">
                        do you want to register as a group?
                    </label>

                    <button
                        type="button"
                        onClick={handleGroupPaymentToggle}
                        role="switch"
                        aria-checked={isGroupPayment}
                        className={`relative inline-flex items-center h-7 w-14 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${isGroupPayment ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                    >
                        <span className="sr-only">
                            {isGroupPayment ? 'Switch to individual' : 'Switch to group'} registration
                        </span>

                        <span
                            className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${isGroupPayment ? 'translate-x-8' : 'translate-x-1'
                                }`}
                        />
                    </button>

                </div>
                <div className=" my-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            {isGroupPayment ? (
                                <GroupIcon className="w-5 h-5 text-purple-600" />
                            ) : (
                                <User className="w-5 h-5 text-purple-600" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">
                                {isGroupPayment ? 'Group Registration' : 'Individual Registration'}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {isGroupPayment
                                    ? "Register multiple members together"
                                    : "Register as a single participant"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Info message based on toggle state */}
                <div className={`mt-3 text-sm p-2 rounded-lg ${isGroupPayment ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                    <div className="flex items-center gap-2">
                        {isGroupPayment ? (
                            <>
                                <GroupIcon className="w-4 h-4 flex-shrink-0" />
                                <span>Group registration enabled. You can add up to {maxMembers} members. All members will share the same participant type.</span>
                            </>
                        ) : (
                            <>
                                <User className="w-4 h-4 flex-shrink-0" />
                                <span>Individual registration. Fill the form below for a single participant.</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Conditional rendering based on isGroupPayment */}
            {isGroupPayment ? (
                /* GROUP REGISTRATION UI - Full group form with add member button */
                <>
                    {/* Header Section */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                            <GroupIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                            <span className="break-words">Group Members ({groupMembers.length}/{maxMembers})</span>
                        </h3>

                        {/* Show Add Member button only if no paid member exists */}
                        {!hasAnyPaidMember() ? (
                            <button
                                type="button"
                                onClick={handleAddMember}
                                disabled={groupMembers.length >= maxMembers}
                                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                <Plus className="w-4 h-4 mr-1 flex-shrink-0" />
                                <span>Add Member</span>
                            </button>
                        ) : (
                            <div className="w-full sm:w-auto text-xs sm:text-sm text-amber-600 bg-amber-50 px-3 sm:px-4 py-2 rounded-lg flex items-center">
                                <Info className="w-4 h-4 mr-1 flex-shrink-0" />
                                <span className="break-words">Paid member present - cannot add more</span>
                            </div>
                        )}
                    </div>

                    {/* Show warning message if there's a paid member */}
                    {hasAnyPaidMember() && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <span className="text-sm sm:text-base text-amber-800 font-medium break-words">
                                    A paid member exists in this group. You cannot add or remove members.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Member Completion Summary - Only show for group mode */}
                    <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Member Completion Status:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {groupMembers.map((member, index) => (
                                <div key={member.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                                        ${isMemberComplete(member) ? 'bg-green-100 text-green-700' :
                                            getMemberCompletionPercentage(member) > 50 ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-red-100 text-red-700'}`}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-700 truncate">
                                            Member {index + 1}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {getMemberCompletionPercentage(member)}% complete
                                        </p>
                                    </div>
                                    {isMemberComplete(member) && (
                                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Render all group members */}
                    {groupMembers.map((member, index) => {
                        const memberPaid = isMemberPaid(member);
                        const isFirstMember = index === 0;
                        const memberComplete = isMemberComplete(member);

                        return (
                            <div key={member.id} className="mb-6 last:mb-0">
                                {/* Member Header */}
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                                    <h4 className="text-sm sm:text-md font-semibold text-gray-800 break-words">
                                        Member {index + 1} {isFirstMember ? '(Lead Member)' : ''}
                                        <span className="ml-2 text-xs font-normal text-gray-500">
                                            {memberPaid ? '(Paid)' :
                                                memberComplete ? '(Complete)' :
                                                    member.verificationStep === 'phone' ? 'Verify Phone' :
                                                        member.verificationStep === 'email' ? 'Verify Email' : 'Enter Details'}
                                        </span>
                                    </h4>

                                    {/* Show remove button only if more than one member AND member is not paid AND no paid member exists */}
                                    {groupMembers.length > 1 && !memberPaid && !hasAnyPaidMember() && (
                                        <button
                                            type="button"
                                            onClick={() => removeGroupMember(member.id)}
                                            className="self-end sm:self-auto p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove member"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Paid Status Message */}
                                {memberPaid && (
                                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-sm sm:text-base text-green-800 font-medium break-words">
                                                This member has already successfully paid for {member.role}. No payment required.
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Failed Payment Message */}
                                {member.isExistingUser && member.paidStatus === 2 &&
                                    member.participantId && member.paidParticipantTypeId &&
                                    parseInt(member.participantId) === parseInt(member.paidParticipantTypeId) && (
                                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <div className="flex items-start gap-2">
                                                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm sm:text-base text-amber-800 font-medium break-words">
                                                    Previous payment failed. You can retry payment for this member.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                {/* Completion Status Banner for Verified Members */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && !memberPaid && !memberComplete && (
                                    <div className="rounded-lg p-4 transition-all duration-300 bg-yellow-50 border border-yellow-200 mb-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-yellow-100 text-yellow-600">
                                                <Info size={18} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-yellow-800 text-sm sm:text-base break-words">
                                                    Complete Required Fields for Member {index + 1}
                                                </h4>
                                                <p className="text-xs sm:text-sm mt-1 text-yellow-700 break-words">
                                                    Fill all required fields to enable submission.
                                                </p>

                                                {/* Field completion status */}
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3 text-xs">
                                                    {['fullName', 'Ins_Name', 'gender', 'Ins_Address', 'PinCode', 'Prefix'].map((field) => (
                                                        <div key={field} className={`flex items-center ${getMemberFieldStatus(member, field) ? 'text-green-600' : 'text-red-500'}`}>
                                                            <span className="mr-1 flex-shrink-0">{getMemberFieldStatus(member, field) ? '✓' : '○'}</span>
                                                            <span className="truncate">
                                                                {field === 'fullName' && 'Full Name'}
                                                                {field === 'Ins_Name' && 'Institution'}
                                                                {field === 'gender' && 'Gender'}
                                                                {field === 'Ins_Address' && 'Address'}
                                                                {field === 'PinCode' && 'Pincode'}
                                                                {field === 'Prefix' && 'Prefix'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <div className={`flex items-center ${member.photoPreview ? 'text-green-600' : 'text-red-500'}`}>
                                                        <span className="mr-1 flex-shrink-0">{member.photoPreview ? '✓' : '○'}</span>
                                                        <span className="truncate">Photo</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Success Banner for Complete Members */}
                                {memberComplete && !memberPaid && (
                                    <div className="rounded-lg p-4 transition-all duration-300 bg-green-50 border border-green-200 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-green-100 text-green-600">
                                                <Check size={18} />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-green-800 text-sm sm:text-base">
                                                    Member {index + 1} is Ready!
                                                </h4>
                                                <p className="text-xs sm:text-sm text-green-700">
                                                    All required fields are completed.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Verification Steps */}
                                {!memberPaid && (
                                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4">
                                        <div className={`flex items-center ${member.verificationStep === 'phone' ? 'text-purple-600' : member.phoneVerified ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'phone' ? 'bg-purple-100 border border-purple-500' : member.phoneVerified ? 'bg-green-100 border border-green-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                {member.phoneVerified ? (
                                                    <Check className="w-3 h-3" />
                                                ) : (
                                                    <Smartphone className="w-3 h-3" />
                                                )}
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Phone</span>
                                        </div>
                                        <div className={`flex items-center ${member.verificationStep === 'email' ? 'text-purple-600' : member.emailVerified ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'email' ? 'bg-purple-100 border border-purple-500' : member.emailVerified ? 'bg-green-100 border border-green-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                {member.emailVerified ? (
                                                    <Check className="w-3 h-3" />
                                                ) : (
                                                    <AtSign className="w-3 h-3" />
                                                )}
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Email</span>
                                        </div>
                                        <div className={`flex items-center ${member.verificationStep === 'details' ? 'text-purple-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'details' ? 'bg-purple-100 border border-purple-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                <User className="w-3 h-3" />
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Details</span>
                                        </div>
                                    </div>
                                )}

                                {/* Profile Photo Upload Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                            <span className="break-words">Profile Photo <span className='text-xs text-blue-700 ml-2'>(Click the profile image to upload)</span> <span className="text-red-600 text-sm ml-1">*</span></span>
                                        </h4>
                                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
                                            <div className="relative w-fit mx-auto sm:mx-0">
                                                <label
                                                    htmlFor={`profile-photo-${member.id}`}
                                                    className={`block cursor-pointer group ${memberPaid ? 'cursor-default' : ''}`}
                                                >
                                                    <div
                                                        className={`bg-black border-2 border-spacing-4 rounded-full p-2 transition-all duration-300
                                                            ${memberPaid
                                                                ? 'border-gray-300 bg-gray-100'
                                                                : 'border-gray-300 hover:border-blue-400 hover:shadow-lg'
                                                            }`}
                                                    >
                                                        <div className="flex items-center justify-center">
                                                            <div
                                                                className={`relative rounded-full bg-gradient-to-br from-gray-50 to-gray-100 border transition-all duration-300
                                                                    ${memberPaid
                                                                        ? 'border-gray-200'
                                                                        : 'border-gray-200 group-hover:border-blue-200'
                                                                    }`}
                                                            >
                                                                {member.photoPreview ? (
                                                                    <div className="relative">
                                                                        <img
                                                                            src={member.photoPreview}
                                                                            alt="Profile preview"
                                                                            className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-full shadow-md ring-4 ring-white"
                                                                        />
                                                                        {!memberPaid && (
                                                                            <>
                                                                                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                                                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                    <div className="bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs font-semibold shadow-lg whitespace-nowrap">
                                                                                        Change Photo
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-col items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-2">
                                                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2 shadow-sm">
                                                                            <Camera size={memberPaid ? 20 : 26} className={`${memberPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                                                                        </div>
                                                                        <p className={`text-xs font-medium text-center ${memberPaid ? 'text-gray-400' : 'text-gray-700'}`}>
                                                                            {memberPaid ? 'Profile Photo' : 'Upload Photo'}
                                                                        </p>
                                                                        {!memberPaid && (
                                                                            <p className="text-xs text-gray-500 mt-1 text-center">
                                                                                JPG, PNG, WEBP
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Only show file input if not paid */}
                                                    {!memberPaid && (
                                                        <input
                                                            id={`profile-photo-${member.id}`}
                                                            type="file"
                                                            className="hidden"
                                                            accept="image/*"
                                                            onChange={(e) => handleMemberFileSelect(e, member.id)}
                                                            disabled={getFieldDisabledStatus('photo', member)}
                                                            ref={el => fileInputRefs.current[member.id] = el}
                                                        />
                                                    )}
                                                </label>

                                                {/* Show paid indicator overlay for paid members without photo */}
                                                {memberPaid && !member.photoPreview && (
                                                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-1 rounded-full">
                                                        <Check className="w-4 h-4" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                                                {memberPaid ? (
                                                    <>
                                                        <p className="font-semibold mb-2 text-green-600 flex items-center justify-center sm:justify-start">
                                                            <Check className="w-4 h-4 mr-1 flex-shrink-0" />
                                                            <span className="break-words">Paid Member - Photo Locked</span>
                                                        </p>
                                                        <p className="break-words">This member has already completed their registration. Profile photo cannot be modified.</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="font-semibold mb-2">Photo Requirements:</p>
                                                        <ul className="list-disc pl-5 space-y-1 text-left">
                                                            <li className="break-words">File size: Less than 512kb</li>
                                                            <li className="break-words">Formats: JPG, PNG</li>
                                                            <li className="break-words">Clear frontal face photo preferred</li>
                                                            <li className="break-words">Light background recommended</li>
                                                        </ul>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Contact Information Section */}
                                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                        <Smartphone className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                        <span className="break-words">Contact Information</span>
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Phone Number */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                Phone Number
                                                <span className="text-red-600 text-sm ml-1">*</span>
                                            </label>

                                            <div className="relative">
                                                <div
                                                    className={`flex border rounded-lg overflow-hidden transition-all duration-200
            ${memberPaid
                                                            ? "border-gray-200 bg-gray-100"
                                                            : member.phoneVerified
                                                                ? "border-green-500 bg-green-50"
                                                                : "border-gray-300 focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-200"
                                                        }`}
                                                >
                                                    {/* Country Code Dropdown */}
                                                    <select
                                                        name="countryCode"
                                                        value={member.countryCode || "+91"}
                                                        onChange={(e) => {
                                                            handleGroupMemberChange(e, member.id);
                                                            // Clear phone when country changes
                                                            if (member.phone) {
                                                                handleGroupMemberChange({
                                                                    target: { name: 'phone', value: '' }
                                                                }, member.id);
                                                            }
                                                        }}
                                                        disabled={member.phoneVerified || member.isExistingUser || memberPaid}
                                                        className={`px-2 py-2 text-sm border-r outline-none min-w-[90px]
                    ${memberPaid
                                                                ? "bg-gray-100 text-gray-500"
                                                                : member.phoneVerified
                                                                    ? "bg-green-50"
                                                                    : "bg-gray-50"
                                                            }`}
                                                    >
                                                        <option value="">Select Code</option>
                                                        {countries.map((country) => (
                                                            <option
                                                                key={`${country.code}-${country.dialCode}`}
                                                                value={country.dialCode}
                                                            >
                                                                {country.code} {country.dialCode}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {/* Phone Number Input */}
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={member.phone || ""}
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/\D/g, '');
                                                            const countryCode = member.countryCode || "+91";

                                                            // For Indian numbers (+91), only allow 10 digits
                                                            if (countryCode === '+91') {
                                                                if (value.length <= 10) {
                                                                    handleGroupMemberChange({
                                                                        target: { name: 'phone', value: value }
                                                                    }, member.id);
                                                                }
                                                            } else {
                                                                // For international numbers, allow up to 15 digits
                                                                if (value.length <= 15) {
                                                                    handleGroupMemberChange({
                                                                        target: { name: 'phone', value: value }
                                                                    }, member.id);
                                                                }
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            // Validate Indian numbers have 10 digits
                                                            const countryCode = member.countryCode || "+91";
                                                            const phoneValue = e.target.value;

                                                            if (countryCode === '+91' && phoneValue && phoneValue.length !== 10) {
                                                                Swal.fire({
                                                                    title: 'Invalid Phone Number',
                                                                    text: 'Please enter a valid 10-digit mobile number',
                                                                    icon: 'warning',
                                                                    confirmButtonText: 'OK',
                                                                    timer: 2000
                                                                });
                                                            }
                                                            if (handlePhoneBlur) {
                                                                handlePhoneBlur(member.id, phoneValue);
                                                            }
                                                        }}
                                                        required
                                                        autoComplete="off"
                                                        disabled={member.phoneVerified === true || member.isExistingUser || memberPaid}
                                                        className={`flex-1 px-3 py-2 text-sm outline-none bg-transparent
                    ${member.phoneVerified === true ? 'text-green-700 font-medium' : ''}
                `}
                                                        placeholder={memberPaid ? "Already verified" : (member.countryCode === '+91' ? "Enter 10-digit number" : "Enter phone number")}
                                                        readOnly={memberPaid || member.phoneVerified === true}
                                                    />
                                                </div>

                                                {verificationInProgress && member.phone && member.phone.length === 10 && !memberPaid && (
                                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                        <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                                                    </div>
                                                )}
                                            </div>

                                            {!memberPaid && (
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {member.phoneVerified ? (
                                                            <span className="flex items-center text-green-600">
                                                                <CheckSquare className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="break-words">
                                                                    Verified ✓
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            member.countryCode === '+91'
                                                                ? `Enter 10-digit number for ${member.countryCode}`
                                                                : `Enter phone number`
                                                        )}
                                                    </p>

                                                    {member.phone &&
                                                        !member.phoneVerified &&
                                                        !member.isExistingUser &&
                                                        !memberPaid && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const countryCode = member.countryCode || "+91";
                                                                    const fullNumber = `${countryCode} ${member.phone}`;

                                                                    // For Indian numbers, validate 10 digits before sending OTP
                                                                    if (countryCode === '+91' && member.phone.length !== 10) {
                                                                        Swal.fire({
                                                                            title: 'Invalid Phone Number',
                                                                            text: 'Please enter a valid 10-digit mobile number',
                                                                            icon: 'warning',
                                                                            confirmButtonText: 'OK'
                                                                        });
                                                                        return;
                                                                    }
                                                                    handleVerifyContact("mobile", fullNumber, member.id);
                                                                }}
                                                                disabled={otpSending || (member.countryCode === '+91' && member.phone.length !== 10)}
                                                                className="w-full sm:w-auto px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {otpSending ? (
                                                                    <>
                                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                        Sending...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Key className="w-3 h-3 mr-1" />
                                                                        Verify
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                </div>
                                            )}

                                            {memberPaid && (
                                                <p className="text-xs text-green-600 flex items-center mt-1">
                                                    <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                    <span className="break-words">Verified and paid</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Email Address */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                Email Address<span className="text-red-600 text-sm ml-1">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={member.email}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('email', member) || member.emailVerified || member.isExistingUser || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'border-gray-200 bg-gray-100 text-gray-500' :
                                                            member.emailVerified ? 'border-green-500 bg-green-50' :
                                                                getFieldDisabledStatus('email', member) ? 'border-gray-300 bg-gray-100' :
                                                                    'border-gray-300 focus:border-purple-500 focus:ring-purple-200'}`}
                                                    placeholder={memberPaid ? "Already verified" : "Enter member's email"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('email', member) && !member.isExistingUser && !memberPaid && (
                                                    <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center">
                                                        <span className="text-xs text-gray-500">Verify phone first</span>
                                                    </div>
                                                )}
                                            </div>
                                            {!memberPaid && (
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {member.emailVerified ? (
                                                            <span className="flex items-center text-green-600">
                                                                <CheckSquare className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="break-words">{member.isExistingUser ? 'Auto-verified' : 'Verified'}</span>
                                                            </span>
                                                        ) : (
                                                            member.phoneVerified || member.isExistingUser ? 'Enter email' : 'Verify phone first'
                                                        )}
                                                    </p>
                                                    {member.email && !member.emailVerified && (member.phoneVerified || member.isExistingUser) && !member.isExistingUser && !memberPaid && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVerifyContact('email', member.email, member.id)}
                                                            disabled={otpSending}
                                                            className="w-full sm:w-auto px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center"
                                                        >
                                                            {otpSending ? (
                                                                <>
                                                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                    Sending...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Key className="w-3 h-3 mr-1" />
                                                                    Verify
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {memberPaid && (
                                                <p className="text-xs text-green-600 flex items-center mt-1">
                                                    <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                    <span className="break-words">Verified and paid</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Personal Details Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                            <span className="break-words">Personal Details</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Prefix and Full Name */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Users className="w-3 h-3 mr-1 text-blue-600" />
                                                    Prefix & Full Name<span className="text-red-600 text-sm ml-1">*</span>
                                                </label>
                                                <div className="md:flex ">
                                                    <select
                                                        name="Prefix"
                                                        value={member.Prefix || ''}
                                                        onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                        required
                                                        disabled={getFieldDisabledStatus('Prefix', member) || memberPaid}
                                                        className={`md:w-1/4 w-full px-3 py-2 text-sm border border-gray-300 rounded-l-lg outline-none focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                'focus:border-blue-500 focus:ring-blue-200'}`}
                                                    >
                                                        <option value="">Prefix</option>
                                                        {honorificsOptions.map((honorific) => (
                                                            <option key={honorific.PrefixID} value={honorific.PrefixID}>
                                                                {honorific.PrefixName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="text"
                                                        name="fullName"
                                                        value={member.fullName}
                                                        onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                        required
                                                        autoComplete='off'
                                                        disabled={getFieldDisabledStatus('fullName', member) || memberPaid}
                                                        className={`flex-1 w-full md:py-2 px-3 py-2 text-sm border outline-none rounded-r-lg focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                getFieldDisabledStatus('fullName', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                    'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                                        placeholder={memberPaid ? "Already registered" : "Enter member's full name"}
                                                        readOnly={memberPaid}
                                                    />
                                                </div>
                                                {getFieldDisabledStatus('fullName', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>

                                            {/* Gender Field */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Users className="w-3 h-3 mr-1 text-green-600" />
                                                    Gender<span className="text-red-600 text-sm ml-1">*</span>
                                                </label>
                                                <select
                                                    name="gender"
                                                    value={member.gender || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    disabled={getFieldDisabledStatus('gender', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            'focus:border-green-500 focus:ring-green-200'}`}
                                                >
                                                    <option value="">Select gender</option>
                                                    {genderOptions.map((gender) => (
                                                        <option key={gender.value} value={gender.value}>
                                                            {gender.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {getFieldDisabledStatus('gender', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Professional Information */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <Building className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                                            <span className="break-words">Professional Information</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Participant Type - Only show for first member */}
                                            {isFirstMember ? (
                                                <div className="space-y-2">
                                                    <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                        Participant Type<span className="text-red-600 text-sm ml-1">*</span>
                                                    </label>
                                                    <select
                                                        name="role"
                                                        value={member.role}
                                                        onChange={(e) => {
                                                            handleGroupMemberChange(e, member.id);
                                                            // Trigger a form data refresh when participant type changes
                                                            if (onFormDataChange) {
                                                                setTimeout(() => {
                                                                    onFormDataChange(prev => ({ ...prev }));
                                                                }, 100);
                                                            }
                                                        }}
                                                        required
                                                        disabled={
                                                            getFieldDisabledStatus('role', member) ||
                                                            memberPaid
                                                        }
                                                        className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                getFieldDisabledStatus('role', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                    'border-gray-300 focus:border-purple-500 focus:ring-purple-200'}`}
                                                    >
                                                        <option value="">Select participant type</option>
                                                        {participantTypes.map((participant) => {
                                                            // Check if this specific participant type is disabled for THIS member only
                                                            const isDisabled = isParticipantTypeDisabled(participant.Participant_ID, member, index);

                                                            return (
                                                                <option
                                                                    key={participant.Participant_ID}
                                                                    value={participant.Participant_Name}
                                                                    disabled={isDisabled}
                                                                    className={isDisabled ? 'bg-gray-100 text-gray-400' : ''}
                                                                >
                                                                    {getParticipantOptionDisplay(participant, member, index)}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                    {memberPaid && (
                                                        <p className="text-xs text-green-600 mt-1 flex items-center">
                                                            <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                            <span className="break-words">Already paid - cannot change</span>
                                                        </p>
                                                    )}
                                                    {getFieldDisabledStatus('role', member) && !memberPaid && (
                                                        <p className="text-xs text-gray-500">
                                                            Verify phone and email first
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                // For non-first members, show read-only participant type
                                                <div className="space-y-2">
                                                    <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                        Participant Type<span className="text-red-600 text-sm ml-1">*</span>
                                                    </label>
                                                    <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 flex flex-wrap items-center gap-2">
                                                        <span className="font-medium break-words">{getParticipantTypeName(member)}</span>
                                                        {memberPaid ? (
                                                            <span className="text-xs text-green-600 flex items-center">
                                                                <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="whitespace-nowrap">Paid</span>
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-blue-600 flex items-center">
                                                                <Info className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="whitespace-nowrap">Auto-filled from lead</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Hidden inputs to ensure form submission includes the data */}
                                                    <input
                                                        type="hidden"
                                                        name="role"
                                                        value={member.role || groupMembers[0]?.role || ''}
                                                    />
                                                    <input
                                                        type="hidden"
                                                        name="participantId"
                                                        value={member.participantId || groupMembers[0]?.participantId || ''}
                                                    />
                                                </div>
                                            )}

                                            {/* Institution Name */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Building className="w-3 h-3 mr-1 text-blue-600 flex-shrink-0" />
                                                    <span className="break-words">Institution Name<span className="text-red-600 text-sm ml-1">*</span></span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="Ins_Name"
                                                    value={member.Ins_Name || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('Ins_Name', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            getFieldDisabledStatus('Ins_Name', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                                    placeholder={memberPaid ? "Already registered" : "Enter institution name"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('Ins_Name', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Institution Address Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <Building className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                                            <span className="break-words">Institution Details</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Institution Address */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <MapPin className="w-3 h-3 mr-1 text-green-600 flex-shrink-0" />
                                                    <span className="break-words">Institution Address<span className="text-red-600 text-sm ml-1">*</span></span>
                                                </label>
                                                <textarea
                                                    name="Ins_Address"
                                                    value={member.Ins_Address || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    rows="3"
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('Ins_Address', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            getFieldDisabledStatus('Ins_Address', member) ? 'bg-gray-100' :
                                                                'focus:border-green-500 focus:ring-green-200'}`}
                                                    placeholder={memberPaid ? "Already registered" : "Enter complete institution address"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('Ins_Address', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                            {/* Pincode and State/District */}
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                        <Hash className="w-3 h-3 mr-1 text-green-600 flex-shrink-0" />
                                                        <span className="break-words">Pincode<span className="text-red-600 text-sm ml-1">*</span></span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="PinCode"
                                                        value={member.PinCode || ''}
                                                        onChange={(e) => handleMemberPincodeChange(e, member.id)}
                                                        maxLength={6}
                                                        required
                                                        autoComplete='off'
                                                        onKeyDown={(e) => {
                                                            // Allow only numbers and control keys
                                                            if (
                                                                !/[0-9]/.test(e.key) &&
                                                                e.key !== 'Backspace' &&
                                                                e.key !== 'Tab' &&
                                                                e.key !== 'ArrowLeft' &&
                                                                e.key !== 'ArrowRight'
                                                            ) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        disabled={getFieldDisabledStatus('PinCode', member) || memberPaid}
                                                        className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                getFieldDisabledStatus('PinCode', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                    'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                        placeholder={memberPaid ? "Already registered" : "6-digit pincode"}
                                                        readOnly={memberPaid}
                                                    />
                                                    {getFieldDisabledStatus('PinCode', member) && !memberPaid && (
                                                        <p className="text-xs text-gray-500">
                                                            Verify phone and email first
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                            State<span className="text-red-600 text-sm ml-1">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="State"
                                                            value={member.State || ''}
                                                            onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                            required
                                                            autoComplete='off'
                                                            disabled={getFieldDisabledStatus('State', member) || memberPaid}
                                                            className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                                ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                    getFieldDisabledStatus('State', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                        'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                            placeholder={memberPaid ? "Auto-filled" : "State"}
                                                            readOnly={memberPaid}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                            District<span className="text-red-600 text-sm ml-1">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="District"
                                                            value={member.District || ''}
                                                            onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                            required
                                                            autoComplete='off'
                                                            disabled={getFieldDisabledStatus('District', member) || memberPaid}
                                                            className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                                ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                    getFieldDisabledStatus('District', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                        'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                            placeholder={memberPaid ? "Auto-filled" : "District"}
                                                            readOnly={memberPaid}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Info for each member */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                <Info className="w-3 h-3 mr-1 text-blue-600 flex-shrink-0" />
                                                <span className="break-words">Additional Notes (Optional)</span>
                                            </label>
                                            <textarea
                                                name="Comments"
                                                value={member.Comments || ''}
                                                onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                rows="2"
                                                autoComplete='off'
                                                disabled={memberPaid}
                                                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 
                                                    ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                                                placeholder={memberPaid ? "Already registered" : "Any additional information for this member..."}
                                                readOnly={memberPaid}
                                            />
                                            <p className="text-xs text-gray-500 break-words">
                                                Optional: Add any special requirements, dietary restrictions, or other notes
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {index < groupMembers.length - 1 && (
                                    <div className="border-b border-gray-200 my-6"></div>
                                )}
                            </div>
                        );
                    })}
                </>
            ) : (
                /* INDIVIDUAL REGISTRATION UI - Single member only, no group features */
                <>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <User className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm sm:text-base text-blue-800 font-medium break-words">
                                Individual Registration - Complete the form below for a single participant
                            </span>
                        </div>
                    </div>

                    {/* Show only the first member's form without any group features */}
                    {groupMembers.length > 0 && (() => {
                        const member = groupMembers[0];
                        const memberPaid = isMemberPaid(member);
                        const memberComplete = isMemberComplete(member);
                        const index = 0;

                        return (
                            <div key={member.id} className="mb-6">
                                {/* Simple header for individual */}
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                                    <h4 className="text-sm sm:text-md font-semibold text-gray-800 break-words">
                                        Participant Details
                                        <span className="ml-2 text-xs font-normal text-gray-500">
                                            {memberPaid ? '(Paid)' :
                                                memberComplete ? '(Complete)' :
                                                    member.verificationStep === 'phone' ? 'Verify Phone' :
                                                        member.verificationStep === 'email' ? 'Verify Email' : 'Enter Details'}
                                        </span>
                                    </h4>
                                </div>

                                {/* Paid Status Message */}
                                {memberPaid && (
                                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <span className="text-sm sm:text-base text-green-800 font-medium break-words">
                                                This participant has already successfully paid for {member.role}. No payment required.
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Failed Payment Message */}
                                {member.isExistingUser && member.paidStatus === 2 &&
                                    member.participantId && member.paidParticipantTypeId &&
                                    parseInt(member.participantId) === parseInt(member.paidParticipantTypeId) && (
                                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <div className="flex items-start gap-2">
                                                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm sm:text-base text-amber-800 font-medium break-words">
                                                    Previous payment failed. You can retry payment.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                {/* Completion Status Banner */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && !memberPaid && !memberComplete && (
                                    <div className="rounded-lg p-4 transition-all duration-300 bg-yellow-50 border border-yellow-200 mb-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-yellow-100 text-yellow-600">
                                                <Info size={18} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-yellow-800 text-sm sm:text-base break-words">
                                                    Complete Required Fields
                                                </h4>
                                                <p className="text-xs sm:text-sm mt-1 text-yellow-700 break-words">
                                                    Fill all required fields to enable submission.
                                                </p>

                                                {/* Field completion status */}
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3 text-xs">
                                                    {['fullName', 'Ins_Name', 'gender', 'Ins_Address', 'PinCode', 'Prefix'].map((field) => (
                                                        <div key={field} className={`flex items-center ${getMemberFieldStatus(member, field) ? 'text-green-600' : 'text-red-500'}`}>
                                                            <span className="mr-1 flex-shrink-0">{getMemberFieldStatus(member, field) ? '✓' : '○'}</span>
                                                            <span className="truncate">
                                                                {field === 'fullName' && 'Full Name'}
                                                                {field === 'Ins_Name' && 'Institution'}
                                                                {field === 'gender' && 'Gender'}
                                                                {field === 'Ins_Address' && 'Address'}
                                                                {field === 'PinCode' && 'Pincode'}
                                                                {field === 'Prefix' && 'Prefix'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <div className={`flex items-center ${member.photoPreview ? 'text-green-600' : 'text-red-500'}`}>
                                                        <span className="mr-1 flex-shrink-0">{member.photoPreview ? '✓' : '○'}</span>
                                                        <span className="truncate">Photo</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Success Banner */}
                                {memberComplete && !memberPaid && (
                                    <div className="rounded-lg p-4 transition-all duration-300 bg-green-50 border border-green-200 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-green-100 text-green-600">
                                                <Check size={18} />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-green-800 text-sm sm:text-base">
                                                    Ready for Submission!
                                                </h4>
                                                <p className="text-xs sm:text-sm text-green-700">
                                                    All required fields are completed.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Verification Steps */}
                                {!memberPaid && (
                                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4">
                                        <div className={`flex items-center ${member.verificationStep === 'phone' ? 'text-purple-600' : member.phoneVerified ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'phone' ? 'bg-purple-100 border border-purple-500' : member.phoneVerified ? 'bg-green-100 border border-green-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                {member.phoneVerified ? (
                                                    <Check className="w-3 h-3" />
                                                ) : (
                                                    <Smartphone className="w-3 h-3" />
                                                )}
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Phone</span>
                                        </div>
                                        <div className={`flex items-center ${member.verificationStep === 'email' ? 'text-purple-600' : member.emailVerified ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'email' ? 'bg-purple-100 border border-purple-500' : member.emailVerified ? 'bg-green-100 border border-green-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                {member.emailVerified ? (
                                                    <Check className="w-3 h-3" />
                                                ) : (
                                                    <AtSign className="w-3 h-3" />
                                                )}
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Email</span>
                                        </div>
                                        <div className={`flex items-center ${member.verificationStep === 'details' ? 'text-purple-600' : 'text-gray-400'}`}>
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${member.verificationStep === 'details' ? 'bg-purple-100 border border-purple-500' : 'bg-gray-100 border border-gray-300'}`}>
                                                <User className="w-3 h-3" />
                                            </div>
                                            <span className="text-xs whitespace-nowrap">Details</span>
                                        </div>
                                    </div>
                                )}

                                {/* Profile Photo Upload Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                            <span className="break-words">Profile Photo <span className='text-xs text-blue-700 ml-2'>(Click the profile image to upload)</span> <span className="text-red-600 text-sm ml-1">*</span></span>
                                        </h4>
                                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
                                            <div className="relative w-fit mx-auto sm:mx-0">
                                                <label
                                                    htmlFor={`profile-photo-${member.id}`}
                                                    className={`block cursor-pointer group ${memberPaid ? 'cursor-default' : ''}`}
                                                >
                                                    <div
                                                        className={`bg-black border-2 border-spacing-4 rounded-full p-2 transition-all duration-300
                                                            ${memberPaid
                                                                ? 'border-gray-300 bg-gray-100'
                                                                : 'border-gray-300 hover:border-blue-400 hover:shadow-lg'
                                                            }`}
                                                    >
                                                        <div className="flex items-center justify-center">
                                                            <div
                                                                className={`relative rounded-full bg-gradient-to-br from-gray-50 to-gray-100 border transition-all duration-300
                                                                    ${memberPaid
                                                                        ? 'border-gray-200'
                                                                        : 'border-gray-200 group-hover:border-blue-200'
                                                                    }`}
                                                            >
                                                                {member.photoPreview ? (
                                                                    <div className="relative">
                                                                        <img
                                                                            src={member.photoPreview}
                                                                            alt="Profile preview"
                                                                            className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-full shadow-md ring-4 ring-white"
                                                                        />
                                                                        {!memberPaid && (
                                                                            <>
                                                                                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                                                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                    <div className="bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs font-semibold shadow-lg whitespace-nowrap">
                                                                                        Change Photo
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-col items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-2">
                                                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2 shadow-sm">
                                                                            <Camera size={memberPaid ? 20 : 26} className={`${memberPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                                                                        </div>
                                                                        <p className={`text-xs font-medium text-center ${memberPaid ? 'text-gray-400' : 'text-gray-700'}`}>
                                                                            {memberPaid ? 'Profile Photo' : 'Upload Photo'}
                                                                        </p>
                                                                        {!memberPaid && (
                                                                            <p className="text-xs text-gray-500 mt-1 text-center">
                                                                                JPG, PNG, WEBP
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Only show file input if not paid */}
                                                    {!memberPaid && (
                                                        <input
                                                            id={`profile-photo-${member.id}`}
                                                            type="file"
                                                            className="hidden"
                                                            accept="image/*"
                                                            onChange={(e) => handleMemberFileSelect(e, member.id)}
                                                            disabled={getFieldDisabledStatus('photo', member)}
                                                            ref={el => fileInputRefs.current[member.id] = el}
                                                        />
                                                    )}
                                                </label>

                                                {/* Show paid indicator overlay for paid members without photo */}
                                                {memberPaid && !member.photoPreview && (
                                                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-1 rounded-full">
                                                        <Check className="w-4 h-4" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                                                {memberPaid ? (
                                                    <>
                                                        <p className="font-semibold mb-2 text-green-600 flex items-center justify-center sm:justify-start">
                                                            <Check className="w-4 h-4 mr-1 flex-shrink-0" />
                                                            <span className="break-words">Paid Member - Photo Locked</span>
                                                        </p>
                                                        <p className="break-words">This participant has already completed their registration. Profile photo cannot be modified.</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="font-semibold mb-2">Photo Requirements:</p>
                                                        <ul className="list-disc pl-5 space-y-1 text-left">
                                                            <li className="break-words">File size: Less than 512kb</li>
                                                            <li className="break-words">Formats: JPG, PNG</li>
                                                            <li className="break-words">Clear frontal face photo preferred</li>
                                                            <li className="break-words">Light background recommended</li>
                                                        </ul>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Contact Information Section */}
                                <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                        <Smartphone className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                        <span className="break-words">Contact Information</span>
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Phone Number */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                Phone Number
                                                <span className="text-red-600 text-sm ml-1">*</span>
                                            </label>

                                            <div className="relative">
                                                <div
                                                    className={`flex border rounded-lg overflow-hidden transition-all duration-200
            ${memberPaid
                                                            ? "border-gray-200 bg-gray-100"
                                                            : member.phoneVerified
                                                                ? "border-green-500 bg-green-50"
                                                                : "border-gray-300 focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-200"
                                                        }`}
                                                >
                                                    {/* Country Code Dropdown */}
                                                    <select
                                                        name="countryCode"
                                                        value={member.countryCode || "+91"}
                                                        onChange={(e) => {
                                                            handleGroupMemberChange(e, member.id);
                                                            // Clear phone when country changes
                                                            if (member.phone) {
                                                                handleGroupMemberChange({
                                                                    target: { name: 'phone', value: '' }
                                                                }, member.id);
                                                            }
                                                        }}
                                                        disabled={member.phoneVerified || member.isExistingUser || memberPaid}
                                                        className={`px-2 py-2 text-sm border-r outline-none min-w-[90px]
                    ${memberPaid
                                                                ? "bg-gray-100 text-gray-500"
                                                                : member.phoneVerified
                                                                    ? "bg-green-50"
                                                                    : "bg-gray-50"
                                                            }`}
                                                    >
                                                        <option value="">Select Code</option>
                                                        {countries.map((country) => (
                                                            <option
                                                                key={`${country.code}-${country.dialCode}`}
                                                                value={country.dialCode}
                                                            >
                                                                {country.code} {country.dialCode}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {/* Phone Number Input */}
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={member.phone || ""}
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/\D/g, '');
                                                            const countryCode = member.countryCode || "+91";

                                                            // For Indian numbers (+91), only allow 10 digits
                                                            if (countryCode === '+91') {
                                                                if (value.length <= 10) {
                                                                    handleGroupMemberChange({
                                                                        target: { name: 'phone', value: value }
                                                                    }, member.id);
                                                                }
                                                            } else {
                                                                // For international numbers, allow up to 15 digits
                                                                if (value.length <= 15) {
                                                                    handleGroupMemberChange({
                                                                        target: { name: 'phone', value: value }
                                                                    }, member.id);
                                                                }
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            // Validate Indian numbers have 10 digits
                                                            const countryCode = member.countryCode || "+91";
                                                            const phoneValue = e.target.value;

                                                            if (countryCode === '+91' && phoneValue && phoneValue.length !== 10) {
                                                                Swal.fire({
                                                                    title: 'Invalid Phone Number',
                                                                    text: 'Please enter a valid 10-digit mobile number',
                                                                    icon: 'warning',
                                                                    confirmButtonText: 'OK',
                                                                    timer: 2000
                                                                });
                                                            }
                                                            if (handlePhoneBlur) {
                                                                handlePhoneBlur(member.id, phoneValue);
                                                            }
                                                        }}
                                                        required
                                                        autoComplete="off"
                                                        disabled={member.phoneVerified === true || member.isExistingUser || memberPaid}
                                                        className={`flex-1 px-3 py-2 text-sm outline-none bg-transparent
                    ${member.phoneVerified === true ? 'text-green-700 font-medium' : ''}
                `}
                                                        placeholder={memberPaid ? "Already verified" : (member.countryCode === '+91' ? "Enter 10-digit number" : "Enter phone number")}
                                                        readOnly={memberPaid || member.phoneVerified === true}
                                                    />
                                                </div>

                                                {verificationInProgress && member.phone && member.phone.length === 10 && !memberPaid && (
                                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                        <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                                                    </div>
                                                )}
                                            </div>

                                            {!memberPaid && (
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {member.phoneVerified ? (
                                                            <span className="flex items-center text-green-600">
                                                                <CheckSquare className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="break-words">
                                                                    Verified ✓
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            member.countryCode === '+91'
                                                                ? `Enter 10-digit number for ${member.countryCode}`
                                                                : `Enter phone number`
                                                        )}
                                                    </p>

                                                    {member.phone &&
                                                        !member.phoneVerified &&
                                                        !member.isExistingUser &&
                                                        !memberPaid && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const countryCode = member.countryCode || "+91";
                                                                    const fullNumber = `${countryCode} ${member.phone}`;

                                                                    // For Indian numbers, validate 10 digits before sending OTP
                                                                    if (countryCode === '+91' && member.phone.length !== 10) {
                                                                        Swal.fire({
                                                                            title: 'Invalid Phone Number',
                                                                            text: 'Please enter a valid 10-digit mobile number',
                                                                            icon: 'warning',
                                                                            confirmButtonText: 'OK'
                                                                        });
                                                                        return;
                                                                    }
                                                                    handleVerifyContact("mobile", fullNumber, member.id);
                                                                }}
                                                                disabled={otpSending || (member.countryCode === '+91' && member.phone.length !== 10)}
                                                                className="w-full sm:w-auto px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {otpSending ? (
                                                                    <>
                                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                        Sending...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Key className="w-3 h-3 mr-1" />
                                                                        Verify
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                </div>
                                            )}

                                            {memberPaid && (
                                                <p className="text-xs text-green-600 flex items-center mt-1">
                                                    <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                    <span className="break-words">Verified and paid</span>
                                                </p>
                                            )}
                                        </div>

                                        {/* Email Address */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                Email Address<span className="text-red-600 text-sm ml-1">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={member.email}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('email', member) || member.emailVerified || member.isExistingUser || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'border-gray-200 bg-gray-100 text-gray-500' :
                                                            member.emailVerified ? 'border-green-500 bg-green-50' :
                                                                getFieldDisabledStatus('email', member) ? 'border-gray-300 bg-gray-100' :
                                                                    'border-gray-300 focus:border-purple-500 focus:ring-purple-200'}`}
                                                    placeholder={memberPaid ? "Already verified" : "Enter email address"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('email', member) && !member.isExistingUser && !memberPaid && (
                                                    <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center">
                                                        <span className="text-xs text-gray-500">Verify phone first</span>
                                                    </div>
                                                )}
                                            </div>
                                            {!memberPaid && (
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                                    <p className="text-xs text-gray-500 break-words">
                                                        {member.emailVerified ? (
                                                            <span className="flex items-center text-green-600">
                                                                <CheckSquare className="w-3 h-3 mr-1 flex-shrink-0" />
                                                                <span className="break-words">{member.isExistingUser ? 'Auto-verified' : 'Verified'}</span>
                                                            </span>
                                                        ) : (
                                                            member.phoneVerified || member.isExistingUser ? 'Enter email' : 'Verify phone first'
                                                        )}
                                                    </p>
                                                    {member.email && !member.emailVerified && (member.phoneVerified || member.isExistingUser) && !member.isExistingUser && !memberPaid && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVerifyContact('email', member.email, member.id)}
                                                            disabled={otpSending}
                                                            className="w-full sm:w-auto px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center"
                                                        >
                                                            {otpSending ? (
                                                                <>
                                                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                    Sending...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Key className="w-3 h-3 mr-1" />
                                                                    Verify
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {memberPaid && (
                                                <p className="text-xs text-green-600 flex items-center mt-1">
                                                    <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                    <span className="break-words">Verified and paid</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Personal Details Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                                            <span className="break-words">Personal Details</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Prefix and Full Name */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Users className="w-3 h-3 mr-1 text-blue-600" />
                                                    Prefix & Full Name<span className="text-red-600 text-sm ml-1">*</span>
                                                </label>
                                                <div className="md:flex">
                                                    <select
                                                        name="Prefix"
                                                        value={member.Prefix || ''}
                                                        onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                        required
                                                        disabled={getFieldDisabledStatus('Prefix', member) || memberPaid}
                                                        className={`md:w-1/4 w-full px-3 py-2 text-sm border border-gray-300 rounded-l-lg outline-none focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                'focus:border-blue-500 focus:ring-blue-200'}`}
                                                    >
                                                        <option value="">Prefix</option>
                                                        {honorificsOptions.map((honorific) => (
                                                            <option key={honorific.PrefixID} value={honorific.PrefixID}>
                                                                {honorific.PrefixName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="text"
                                                        name="fullName"
                                                        value={member.fullName}
                                                        onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                        required
                                                        autoComplete='off'
                                                        disabled={getFieldDisabledStatus('fullName', member) || memberPaid}
                                                        className={`flex-1 w-full px-3 py-2 text-sm border outline-none rounded-r-lg focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                getFieldDisabledStatus('fullName', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                    'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                                        placeholder={memberPaid ? "Already registered" : "Enter full name"}
                                                        readOnly={memberPaid}
                                                    />
                                                </div>
                                                {getFieldDisabledStatus('fullName', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>

                                            {/* Gender Field */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Users className="w-3 h-3 mr-1 text-green-600" />
                                                    Gender<span className="text-red-600 text-sm ml-1">*</span>
                                                </label>
                                                <select
                                                    name="gender"
                                                    value={member.gender || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    disabled={getFieldDisabledStatus('gender', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            'focus:border-green-500 focus:ring-green-200'}`}
                                                >
                                                    <option value="">Select gender</option>
                                                    {genderOptions.map((gender) => (
                                                        <option key={gender.value} value={gender.value}>
                                                            {gender.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {getFieldDisabledStatus('gender', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Professional Information */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <Building className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                                            <span className="break-words">Professional Information</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Participant Type */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                    Participant Type<span className="text-red-600 text-sm ml-1">*</span>
                                                </label>
                                                <select
                                                    name="role"
                                                    value={member.role}
                                                    onChange={(e) => {
                                                        handleGroupMemberChange(e, member.id);
                                                        // Trigger a form data refresh when participant type changes
                                                        if (onFormDataChange) {
                                                            setTimeout(() => {
                                                                onFormDataChange(prev => ({ ...prev }));
                                                            }, 100);
                                                        }
                                                    }}
                                                    required
                                                    disabled={
                                                        getFieldDisabledStatus('role', member) ||
                                                        memberPaid
                                                    }
                                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            getFieldDisabledStatus('role', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                'border-gray-300 focus:border-purple-500 focus:ring-purple-200'}`}
                                                >
                                                    <option value="">Select participant type</option>
                                                    {participantTypes.map((participant) => {
                                                        // Check if this specific participant type is disabled for THIS member only
                                                        const isDisabled = isParticipantTypeDisabled(participant.Participant_ID, member, index);

                                                        return (
                                                            <option
                                                                key={participant.Participant_ID}
                                                                value={participant.Participant_Name}
                                                                disabled={isDisabled}
                                                                className={isDisabled ? 'bg-gray-100 text-gray-400' : ''}
                                                            >
                                                                {getParticipantOptionDisplay(participant, member, index)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {memberPaid && (
                                                    <p className="text-xs text-green-600 mt-1 flex items-center">
                                                        <Check className="w-3 h-3 mr-1 flex-shrink-0" />
                                                        <span className="break-words">Already paid - cannot change</span>
                                                    </p>
                                                )}
                                                {getFieldDisabledStatus('role', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>

                                            {/* Institution Name */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <Building className="w-3 h-3 mr-1 text-blue-600 flex-shrink-0" />
                                                    <span className="break-words">Institution Name<span className="text-red-600 text-sm ml-1">*</span></span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="Ins_Name"
                                                    value={member.Ins_Name || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('Ins_Name', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            getFieldDisabledStatus('Ins_Name', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                'border-gray-300 focus:border-blue-500 focus:ring-blue-200'}`}
                                                    placeholder={memberPaid ? "Already registered" : "Enter institution name"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('Ins_Name', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Institution Address Section */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-4 flex items-center">
                                            <Building className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                                            <span className="break-words">Institution Details</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {/* Institution Address */}
                                            <div className="space-y-2">
                                                <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                    <MapPin className="w-3 h-3 mr-1 text-green-600 flex-shrink-0" />
                                                    <span className="break-words">Institution Address<span className="text-red-600 text-sm ml-1">*</span></span>
                                                </label>
                                                <textarea
                                                    name="Ins_Address"
                                                    value={member.Ins_Address || ''}
                                                    onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                    rows="3"
                                                    required
                                                    autoComplete='off'
                                                    disabled={getFieldDisabledStatus('Ins_Address', member) || memberPaid}
                                                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 transition-all duration-200 
                                                        ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                            getFieldDisabledStatus('Ins_Address', member) ? 'bg-gray-100' :
                                                                'focus:border-green-500 focus:ring-green-200'}`}
                                                    placeholder={memberPaid ? "Already registered" : "Enter complete institution address"}
                                                    readOnly={memberPaid}
                                                />
                                                {getFieldDisabledStatus('Ins_Address', member) && !memberPaid && (
                                                    <p className="text-xs text-gray-500">
                                                        Verify phone and email first
                                                    </p>
                                                )}
                                            </div>
                                            {/* Pincode and State/District */}
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                        <Hash className="w-3 h-3 mr-1 text-green-600 flex-shrink-0" />
                                                        <span className="break-words">Pincode<span className="text-red-600 text-sm ml-1">*</span></span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="PinCode"
                                                        value={member.PinCode || ''}
                                                        onChange={(e) => handleMemberPincodeChange(e, member.id)}
                                                        maxLength={6}
                                                        required
                                                        autoComplete='off'
                                                        onKeyDown={(e) => {
                                                            // Allow only numbers and control keys
                                                            if (
                                                                !/[0-9]/.test(e.key) &&
                                                                e.key !== 'Backspace' &&
                                                                e.key !== 'Tab' &&
                                                                e.key !== 'ArrowLeft' &&
                                                                e.key !== 'ArrowRight'
                                                            ) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        disabled={getFieldDisabledStatus('PinCode', member) || memberPaid}
                                                        className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                            ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                getFieldDisabledStatus('PinCode', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                    'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                        placeholder={memberPaid ? "Already registered" : "6-digit pincode"}
                                                        readOnly={memberPaid}
                                                    />
                                                    {getFieldDisabledStatus('PinCode', member) && !memberPaid && (
                                                        <p className="text-xs text-gray-500">
                                                            Verify phone and email first
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                            State<span className="text-red-600 text-sm ml-1">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="State"
                                                            value={member.State || ''}
                                                            onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                            required
                                                            autoComplete='off'
                                                            disabled={getFieldDisabledStatus('State', member) || memberPaid}
                                                            className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                                ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                    getFieldDisabledStatus('State', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                        'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                            placeholder={memberPaid ? "Auto-filled" : "State"}
                                                            readOnly={memberPaid}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-xs font-semibold text-gray-700 mb-1 block">
                                                            District<span className="text-red-600 text-sm ml-1">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="District"
                                                            value={member.District || ''}
                                                            onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                            required
                                                            autoComplete='off'
                                                            disabled={getFieldDisabledStatus('District', member) || memberPaid}
                                                            className={`w-full px-3 py-2 text-sm border outline-none rounded-lg focus:ring-2 transition-all duration-200 
                                                                ${memberPaid ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed' :
                                                                    getFieldDisabledStatus('District', member) ? 'border-gray-300 bg-gray-100 cursor-not-allowed' :
                                                                        'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                                            placeholder={memberPaid ? "Auto-filled" : "District"}
                                                            readOnly={memberPaid}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Info */}
                                {((member.phoneVerified && member.emailVerified) || member.isExistingUser) && (
                                    <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                                                <Info className="w-3 h-3 mr-1 text-blue-600 flex-shrink-0" />
                                                <span className="break-words">Additional Notes (Optional)</span>
                                            </label>
                                            <textarea
                                                name="Comments"
                                                value={member.Comments || ''}
                                                onChange={(e) => handleGroupMemberChange(e, member.id)}
                                                rows="2"
                                                autoComplete='off'
                                                disabled={memberPaid}
                                                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 
                                                    ${memberPaid ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                                                placeholder={memberPaid ? "Already registered" : "Any additional information..."}
                                                readOnly={memberPaid}
                                            />
                                            <p className="text-xs text-gray-500 break-words">
                                                Optional: Add any special requirements, dietary restrictions, or other notes
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
};

export default GroupForm;